'use client';
import React from 'react';
import { RotateCcw, BookOpen, Share2 } from 'lucide-react';
import { SAMPLE_PASSAGE, type ExamPreset } from '@/data/examPresets';
import type { ExamResult, WordState } from '../lib/scoring';

interface ExamResultsProps {
  exam: ExamPreset;
  result: ExamResult;
  onTryAgain: () => void;
  onNewExam: () => void;
}

const WORDS = SAMPLE_PASSAGE.split(' ');

/** Word marking follows proofreading convention: colour the text, not the box. */
const WORD_STYLE: Record<WordState, React.CSSProperties> = {
  correct: { color: 'var(--foreground)' },
  full: {
    color: 'var(--incorrect)',
    textDecoration: 'underline',
    textDecorationColor: 'rgba(176,42,42,0.5)',
    textUnderlineOffset: 3,
  },
  half: {
    color: 'var(--warning)',
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    textUnderlineOffset: 3,
  },
  omission: { color: '#C4BFB4' },
};

const LEGEND: { state: WordState; label: string }[] = [
  { state: 'correct', label: 'Correct' },
  { state: 'full', label: 'Full mistake' },
  { state: 'half', label: 'Half mistake' },
  { state: 'omission', label: 'Omission' },
];

function Figure({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div
      className="flex flex-col gap-1.5 px-4 py-4"
      style={{ borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}
    >
      <span className="stat-card-label">{label}</span>
      <span
        className="font-mono stat-value leading-none"
        style={{ fontSize: '1.4rem', fontWeight: 500, color: color ?? 'var(--foreground)' }}
      >
        {value}
      </span>
    </div>
  );
}

export default function ExamResults({ exam, result, onTryAgain, onNewExam }: ExamResultsProps) {
  const qualified = result.netWpm >= exam.targetWpm;
  const netWords = result.grossWpm > 0 ? result.keystrokes / 5 - result.netPenalty : 0;

  return (
    <div className="max-w-3xl mx-auto space-y-5 animate-fade-in">
      {/* Verdict */}
      <div
        className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-5 py-4"
        style={{
          border: `1px solid ${qualified ? 'var(--foreground)' : 'var(--incorrect)'}`,
          background: qualified ? 'transparent' : 'var(--incorrect-soft)',
        }}
      >
        <span
          className="eyebrow"
          style={{ color: qualified ? 'var(--foreground)' : 'var(--incorrect)' }}
        >
          {qualified ? 'Qualified' : 'Not qualified'}
        </span>
        <span className="text-sm text-muted-foreground">
          {exam.name} — {exam.targetWpm} WPM target
        </span>
      </div>

      {/* Net WPM sheet */}
      <div className="card-elevated">
        <div className="px-5 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
          <span className="eyebrow">Net WPM</span>
        </div>
        <div className="py-10 text-center">
          <div
            className="font-serif stat-value leading-none"
            style={{
              fontSize: 'clamp(4.5rem, 13vw, 6rem)',
              fontWeight: 600,
              letterSpacing: '-0.03em',
              color: qualified ? 'var(--foreground)' : 'var(--incorrect)',
            }}
          >
            {result.netWpm}
          </div>
          <p className="text-sm text-faint mt-4">
            {result.attemptedWords} of {WORDS.length} words attempted in{' '}
            {Math.floor(result.elapsedSec / 60)}m {result.elapsedSec % 60}s
          </p>
        </div>
      </div>

      {/* Figures */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3"
        style={{ borderTop: '1px solid var(--border)', borderLeft: '1px solid var(--border)' }}
      >
        <Figure label="Gross WPM" value={String(result.grossWpm)} />
        <Figure label="Keystrokes" value={result.keystrokes.toLocaleString()} />
        <Figure label="Accuracy" value={`${result.accuracy}%`} />
        <Figure
          label="Full mistakes"
          value={String(result.fullMistakes)}
          color={result.fullMistakes > 0 ? 'var(--incorrect)' : undefined}
        />
        <Figure
          label="Half mistakes"
          value={String(result.halfMistakes)}
          color={result.halfMistakes > 0 ? 'var(--warning)' : undefined}
        />
        <Figure label="Net penalty" value={result.netPenalty.toFixed(1)} />
      </div>

      {/* SSC formula — shown as worked arithmetic */}
      <div className="card-elevated">
        <div className="px-5 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
          <span className="eyebrow">SSC evaluation formula</span>
        </div>
        <div className="p-5 font-mono-typing text-sm leading-[1.9] text-muted-foreground">
          <div>
            Gross words = {result.keystrokes} ÷ 5 = {(result.keystrokes / 5).toFixed(1)}
          </div>
          <div>
            Net words&nbsp;&nbsp;&nbsp;= {(result.keystrokes / 5).toFixed(1)} − ({' '}
            {result.fullMistakes} + {result.halfMistakes} × 0.5 ) = {netWords.toFixed(1)}
          </div>
          <div
            className="text-foreground mt-2 pt-2"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            Net WPM&nbsp;&nbsp;&nbsp;&nbsp;= {netWords.toFixed(1)} ÷ {result.minutes.toFixed(2)} min
            = {result.netWpm}
          </div>
        </div>
      </div>

      {/* Word-by-word proof */}
      <div className="card-elevated">
        <div
          className="flex flex-wrap items-center justify-between gap-3 px-5 py-2.5"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <span className="eyebrow">Word-by-word analysis</span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {LEGEND.map(({ state, label }) => (
              <span key={state} className="text-[11px] font-mono" style={WORD_STYLE[state]}>
                {label}
              </span>
            ))}
          </div>
        </div>
        <p className="p-5 font-mono-typing text-sm leading-[2]">
          {WORDS.map((word, i) => (
            <span key={`${word}-${i}`} style={WORD_STYLE[result.wordStates[i] ?? 'omission']}>
              {word}{' '}
            </span>
          ))}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
        <button
          type="button"
          onClick={onTryAgain}
          className="btn-primary inline-flex items-center gap-2 px-6 py-2.5 text-sm w-full sm:w-auto justify-center"
        >
          <RotateCcw size={16} /> Try again
        </button>
        <button
          type="button"
          onClick={onNewExam}
          className="btn-secondary inline-flex items-center gap-2 px-6 py-2.5 text-sm w-full sm:w-auto justify-center"
        >
          <BookOpen size={16} /> New exam
        </button>
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-2 px-6 py-2.5 text-sm w-full sm:w-auto justify-center"
        >
          <Share2 size={16} /> Share
        </button>
      </div>
    </div>
  );
}
